import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Send, Bot, User } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { formatWhatsAppMarkdown } from "@/lib/utils";

interface Agent {
  id: string;
  name: string;
  role: string;
  tone: string;
  prompt_template: string;
  is_active: boolean;
}

interface Message {
  role: "user" | "assistant";
  content: string;
}

interface AgentTestChatProps {
  workspaceId: string;
}

export function AgentTestChat({ workspaceId }: AgentTestChatProps) {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedAgentId, setSelectedAgentId] = useState<string>("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchAgents();
  }, [workspaceId]);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const fetchAgents = async () => {
    // Aba Teste lista TODOS os agentes do workspace (inclusive inativos).
    // Agentes inativos só funcionam neste sandbox — produção continua bloqueando.
    const { data } = await supabase
      .from("agents")
      .select("*")
      .eq("workspace_id", workspaceId)
      .order("is_active", { ascending: false })
      .order("name");

    if (data) {
      setAgents(data as Agent[]);
      if (data.length > 0 && !selectedAgentId) {
        setSelectedAgentId(data[0].id);
      }
    }
  };

  const handleSend = async () => {
    if (!input.trim() || !selectedAgentId) return;

    const userMessage: Message = { role: "user", content: input };
    // Paridade com produção: primeira mensagem do usuário na conversa
    const isFirstUserMessage =
      messages.filter((m) => m.role === "user").length === 0;
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke("chat", {
        body: {
          messages: [...messages, userMessage],
          agentId: selectedAgentId,
          isTest: true,
          isFirstUserMessage,
        },
      });

      if (error) throw error;

      if (data?.message) {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: data.message },
        ]);
      }
    } catch (error: any) {
      toast({
        title: "Erro ao enviar mensagem",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setMessages([]);
  };

  const selectedAgent = agents.find((a) => a.id === selectedAgentId);

  return (
    <Card className="flex h-full min-h-0 flex-col overflow-hidden">
      <div className="shrink-0 space-y-4 border-b p-4">
        <div>
          <label className="text-sm font-medium mb-2 block">
            Selecione o Agente
          </label>
          <Select value={selectedAgentId} onValueChange={setSelectedAgentId}>
            <SelectTrigger>
              <SelectValue placeholder="Escolha um agente..." />
            </SelectTrigger>
            <SelectContent>
              {agents.map((agent) => (
                <SelectItem key={agent.id} value={agent.id}>
                  <span className="flex items-center gap-2">
                    <span>{agent.name} - {agent.role}</span>
                    {!agent.is_active && (
                      <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground border">
                        Inativo
                      </span>
                    )}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {selectedAgent && !selectedAgent.is_active && (
          <div className="text-xs rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-900 dark:text-amber-200 px-3 py-2">
            Este agente está <strong>desativado</strong>. Você pode testá-lo aqui,
            mas ele não responde clientes em produção. Para ativar, use o botão
            na página de Agentes de IA.
          </div>
        )}

        {selectedAgent && (
          <div className="text-sm text-muted-foreground space-y-1">
            <p><strong>Tom:</strong> {selectedAgent.tone}</p>
            <p className="line-clamp-2"><strong>Prompt:</strong> {selectedAgent.prompt_template}</p>
          </div>
        )}

        <Button onClick={handleClear} variant="outline" size="sm" className="w-full">
          Limpar Conversa
        </Button>
      </div>

      <ScrollArea className="min-h-0 flex-1 p-4">
        <div className="space-y-4">
          {messages.length === 0 && (
            <div className="text-center text-muted-foreground py-8">
              Inicie uma conversa de teste com o agente selecionado
            </div>
          )}
          
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex gap-3 ${
                message.role === "user" ? "justify-end" : "justify-start"
              }`}
            >
              {message.role === "assistant" && (
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Bot className="w-4 h-4 text-primary" />
                </div>
              )}
              
              <div
                className={`rounded-lg px-4 py-2 max-w-[80%] ${
                  message.role === "user"
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted"
                }`}
              >
                {message.role === "assistant" ? (
                  <span
                    dangerouslySetInnerHTML={{
                      __html: formatWhatsAppMarkdown(message.content),
                    }}
                  />
                ) : (
                  message.content
                )}
              </div>
              
              {message.role === "user" && (
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-primary-foreground" />
                </div>
              )}
            </div>
          ))}
          
          {isLoading && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Bot className="w-4 h-4 text-primary animate-pulse" />
              </div>
              <div className="bg-muted rounded-lg px-4 py-2">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-current rounded-full animate-bounce" />
                  <div className="w-2 h-2 bg-current rounded-full animate-bounce delay-100" />
                  <div className="w-2 h-2 bg-current rounded-full animate-bounce delay-200" />
                </div>
              </div>
            </div>
          )}
          
          <div ref={scrollRef} />
        </div>
      </ScrollArea>

      <div className="shrink-0 border-t p-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSend();
          }}
          className="flex gap-2"
        >
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Digite sua mensagem..."
            disabled={isLoading || !selectedAgentId}
          />
          <Button
            type="submit"
            disabled={isLoading || !input.trim() || !selectedAgentId}
          >
            <Send className="w-4 h-4" />
          </Button>
        </form>
      </div>
    </Card>
  );
}
